/**
 * Authentication Context Provider
 * Manages global authentication state across the app
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Load user on mount if token exists
    useEffect(() => {
        const initAuth = async () => {
            // Check for OAuth callback
            const callbackResult = authService.handleAuthCallback();

            if (callbackResult.success) {
                // Fetch user info after successful OAuth
                const userData = await authService.getCurrentUser();
                if (userData) {
                    setUser(userData);
                    setIsAuthenticated(true);
                }
            } else if (authService.isAuthenticated()) {
                // Token exists, fetch user info
                const userData = await authService.getCurrentUser();
                if (userData) {
                    setUser(userData);
                    setIsAuthenticated(true);
                } else {
                    // Token invalid, clear it
                    authService.removeToken();
                }
            }

            setLoading(false);
        };

        initAuth();
    }, []);

    const login = () => {
        authService.loginWithGoogle();
    };

    const logout = async () => {
        await authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    const refreshUser = async () => {
        const userData = await authService.getCurrentUser();
        if (userData) {
            setUser(userData);
            setIsAuthenticated(true);
        }
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        refreshUser
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
